import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ZONE_COLLECTION } from 'constants/collectionConstants';
import { FARMS_QUERY_KEY } from 'constants/queryKeys';
import type { IFarm } from 'interfaces/IFarm';
import FarmService from 'services/FarmService';

import {
  readCachedFarm,
  readZoneIdsByPaddockIds,
  removeZonesFromCache,
} from './farmsCache';

/**
 * Deletes a farm and drops it from the org's farms cache, so it leaves the
 * Paddocks lens and the tree straight away (OP-2481). Its paddocks go with
 * it — GetByOrgActive no longer returns the farm. All cascade-deleted zones
 * are identified from the response `changes` array and removed from the zones
 * cache (covers default and custom zones for every paddock).
 *
 * Fallback: if `changes` carries no zone records, the farm's own paddock
 * zoneIds are used — consistent with the upsert cache strategy.
 */
export const useDeleteFarm = (orgId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (farmId: string) => FarmService.Delete(farmId),
    onSuccess: (response, farmId) => {
      if (!orgId) return;

      // Read the farm before clearing it — needed for the zone fallback when
      // data[0] is also absent.
      const farmBeforeDeletion =
        (response?.data?.[0] as IFarm | undefined) ??
        readCachedFarm(queryClient, orgId, farmId);

      queryClient.setQueryData<IFarm[]>([FARMS_QUERY_KEY, orgId], (current) =>
        current?.filter((farm) => farm.id !== farmId)
      );

      const deletedZoneIds = (response?.changes ?? [])
        .filter((c) => c.collection === ZONE_COLLECTION)
        .map((c) => c.docId);

      if (deletedZoneIds.length > 0) {
        removeZonesFromCache(queryClient, orgId, deletedZoneIds);
      } else {
        // Fallback: reverse-lookup all zones (default and custom) linked to
        // any of the farm's paddocks via zone.links. paddock.zoneId only
        // covers default zones, so the cache is the authoritative source.
        const paddockIds = (farmBeforeDeletion?.paddocks ?? []).map(
          (p) => p.paddockId
        );
        if (paddockIds.length > 0) {
          const fallbackZoneIds = readZoneIdsByPaddockIds(
            queryClient,
            orgId,
            paddockIds
          );
          if (fallbackZoneIds.length > 0) {
            removeZonesFromCache(queryClient, orgId, fallbackZoneIds);
          }
        }
      }
    },
  });
};

export default useDeleteFarm;
